/**
 * index.tsx
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */
// import './styles/antd_styles.less';
import './styles/antd_styles.css';
import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';
// Use consistent styling
import 'sanitize.css/sanitize.css';
// Initialize dayjs with required plugins
import 'utils/dayjs-config';
// Initialize languages
import './locales/i18n';

import * as React from 'react';
import * as ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';

import { BrowserView, MobileView } from 'react-device-detect';

import FontFaceObserver from 'fontfaceobserver';
import { HelmetProvider } from 'react-helmet-async';
import { Provider } from 'react-redux';
import { ThemeProvider } from 'styles/theme/ThemeProvider';
import { configureAppStore } from 'store/configureStore';
import { lazyLoad } from 'utils/loadable';
import reportWebVitals from 'reportWebVitals';
import ReactGA from 'react-ga4';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AppDefaults } from 'config/global-enums';

if (process.env.REACT_APP_TYPE !== 'dev') {
  Sentry.init({
    dsn: 'https://fa558f5f77e45dc855904037b771e965@o4509425213767680.ingest.us.sentry.io/4509425302241280',
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    integrations: [Sentry.browserTracingIntegration()],
    environment: process.env.REACT_APP_ENV,
    release:
      AppDefaults.APP_NAME +
      ' +' +
      AppDefaults.APP_VERSION +
      '+' +
      AppDefaults.APP_BUILD,
    // tracePropagationTargets: ['https://myproject.org'],
  });
}

ReactGA.initialize('G-E8B856ZZTV');

const SendAnalytics = () => {
  ReactGA.send({
    hitType: 'pageview',
    page: window.location.pathname,
  });
};

const WebApp = lazyLoad(() => import('webApp'));

const MobileApp = lazyLoad(() => import('mobileApp'));

// Observe loading of Source Sans Pro (to remove 'Source Sans Pro', remove the <link> tag in
// the index.html file and this observer)
const openSansObserver = new FontFaceObserver('Source Sans Pro', {
  weight: 'normal',
  style: 'normal',
  stretch: 'normal',
  timeout: 10000, // Increase timeout to 10 seconds
});

// When Source Sans Pro is loaded, add a font-family using Source Sans Pro to the body
openSansObserver
  .load()
  .then(() => {
    document.body.classList.add('fontLoaded');
  })
  .catch(error => {
    // Handle font loading timeout gracefully
    console.warn('Font loading timeout or error:', error.message);
    // Still add the class to prevent UI blocking
    document.body.classList.add('fontLoaded');
  });

const store = configureAppStore();
const MOUNT_NODE = document.getElementById('root');

const ReactRoot = ReactDOM.createRoot(MOUNT_NODE);

if (process.env.REACT_APP_ENV !== 'test') {
  console.log = function () {};
}

// Chunk error handler component
const ChunkErrorHandler = () => {
  React.useEffect(() => {
    const handleChunkError = event => {
      if (event.reason && event.reason.name === 'ChunkLoadError') {
        console.error('Chunk load error:', event.reason);
        alert('Application update available. Please refresh the page.');
        // Optionally reload the page after a short delay
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    };

    const handleScriptError = event => {
      // Handle script loading errors (like SyntaxError: Unexpected token '<')
      if (
        event.filename &&
        event.filename.includes('/static/js/') &&
        event.filename.includes('.chunk.js')
      ) {
        console.error('Chunk script error:', event);
        alert('Application update available. Please refresh the page.');
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    };

    window.addEventListener('unhandledrejection', handleChunkError);
    window.addEventListener('error', handleScriptError);

    return () => {
      window.removeEventListener('unhandledrejection', handleChunkError);
      window.removeEventListener('error', handleScriptError);
    };
  }, []);

  return null; // This component doesn't render anything
};

ReactRoot.render(
  <GoogleOAuthProvider clientId="937690079602-31i5qjthfnbo8nea4s1h4fm8ttj3c11r.apps.googleusercontent.com">
    <Provider store={store}>
      <ThemeProvider>
        <HelmetProvider>
          <ChunkErrorHandler />
          <BrowserView>
            <WebApp />
          </BrowserView>
          <MobileView>
            <MobileApp />
          </MobileView>
        </HelmetProvider>
      </ThemeProvider>
    </Provider>
  </GoogleOAuthProvider>,
);

// Hot reloadable translation json files
if (module.hot) {
  module.hot.accept(['./locales/i18n'], () => {
    // No need to render the App again because i18next works with the hooks
  });
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals(SendAnalytics);

// Global error handler for unhandled promise rejections
window.addEventListener('unhandledrejection', function (event) {
  console.warn('Unhandled promise rejection:', event.reason);

  // Handle network errors specifically
  if (
    event.reason &&
    event.reason.message &&
    event.reason.message.includes('NetworkError')
  ) {
    console.warn('Network error caught and handled:', event.reason.message);
    // Prevent the default browser behavior
    event.preventDefault();
  }

  // For other types of errors, you might want to log them to Sentry
  if (process.env.REACT_APP_TYPE !== 'dev' && event.reason) {
    // Log to Sentry if not in development
    console.error('Unhandled rejection logged to Sentry:', event.reason);
  }
});
